import { useMutation } from "@apollo/client";
import { useState } from "react";
import { FaCheck, FaDiscord, FaQuestionCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import { useProfiles } from "../../../../context/ProfilesProvider";
import {
  GENERATE_DISCORD_TOKEN,
  VALIDATE_DISCORD_TOKEN,
} from "../../../../queries/mutation/discord/discord_auth";

const DiscordLinkWidget = () => {
  const { discordProfile, discordLoading, refetchDiscordProfile } =
    useProfiles();
  const [inputDiscordId, setInputDiscordId] = useState("");
  const [inputToken, setInputToken] = useState("");
  const [hasSendToken, setHasSentToken] = useState(false);
  // Already linked and switching to a different account.
  const [changing, setChanging] = useState(false);

  const resetForm = () => {
    setInputDiscordId("");
    setInputToken("");
    setHasSentToken(false);
    setChanging(false);
  };

  const [generateTokenMutation, { loading: sending }] = useMutation(
    GENERATE_DISCORD_TOKEN,
    {
      onError: (error) => {
        toast.error(error.message);
      },
      onCompleted: () => {
        toast.success("Verification code has been sent to your Discord DMs");
        setHasSentToken(true);
      },
    },
  );
  const [validateTokenMutation, { loading: validating }] = useMutation(
    VALIDATE_DISCORD_TOKEN,
    {
      onError: (error) => {
        toast.error(error.message);
      },
      onCompleted: async () => {
        toast.success(
          changing ? "Discord account changed!" : "Discord Profile Linked! :)",
        );
        resetForm();
        await refetchDiscordProfile();
      },
    },
  );

  const onGenerateToken = async () => {
    const isValidDiscordId = /^\d{17,20}$/.test(inputDiscordId);
    if (!isValidDiscordId) {
      toast.error(`${inputDiscordId} is not a valid discord ID`);
      return;
    }
    if (discordProfile && inputDiscordId === discordProfile.discordId) {
      toast.error("That's the account you already have linked");
      return;
    }
    await generateTokenMutation({ variables: { discordId: inputDiscordId } });
  };

  const onValidateToken = async () => {
    const isValidToken = /^\d{5}$/.test(inputToken);
    if (!isValidToken) {
      toast.error(`${inputToken} is not a valid code`);
      return;
    }
    await validateTokenMutation({
      variables: {
        input: { discordId: inputDiscordId, discordLinkToken: inputToken },
      },
    });
  };

  const showInfoModal = () => {
    (
      document.getElementById("discord_id_info") as HTMLDialogElement
    )?.showModal();
  };

  if (discordLoading) {
    return <div className="loading loading-spinner" />;
  }

  if (discordProfile && !changing) {
    return (
      <div className="card bg-base-100 w-full sm:w-96">
        <div className="card-body border-2 border-success rounded-box">
          <div className="flex flex-col justify-center items-center h-full gap-2">
            <div>Discord Account Linked!</div>
            <div className="text-success flex flex-row gap-2">
              <FaDiscord size={24} />
              <FaCheck size={24} />
            </div>
            <div className="text-sm text-base-content/70 text-center break-all">
              {discordProfile.username ? `@${discordProfile.username}` : null}
              <div className="text-xs">{discordProfile.discordId}</div>
            </div>
            <button
              className="btn btn-outline btn-sm mt-2"
              onClick={() => setChanging(true)}
            >
              Change Discord account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Link form - for a first link, or to switch to a different account.
  return (
    <div className="card bg-base-100 w-full sm:w-96">
      <div className="card-body">
        <div className="card-title justify-between">
          <div>{changing ? "Change Discord Account" : "Link Your Discord Account"}</div>
          <div className="text-primary">
            <FaDiscord size={24} />
          </div>
        </div>
        <div className="divider divider-primary m-0" />
        {changing && (
          <div className="alert alert-info py-2 text-sm">
            <span>
              The new account replaces{" "}
              {discordProfile?.username
                ? `@${discordProfile.username}`
                : "your current one"}
              . If you're VIP, the role moves to the new account. Your
              crimcoins stay with your Reinstorm account.
            </span>
          </div>
        )}
        <label className="form-control w-full max-w-xs">
          <div className="label">
            <span className="label-text">
              <div className="text-primary">Discord Id</div>
              <div>Not Discord username</div>
            </span>
            <span
              className="label-text-alt text-primary cursor-pointer"
              onClick={showInfoModal}
            >
              <FaQuestionCircle size={24} />
            </span>
          </div>
          <input
            type="text"
            placeholder="Paste Your discord Id here"
            className="input input-bordered w-full max-w-xs"
            value={inputDiscordId}
            onChange={(e) => {
              setInputDiscordId(e.target.value.trim());
              // The code was sent to the previous ID, so it can't apply here.
              setHasSentToken(false);
              setInputToken("");
            }}
          />
          <div className="label">
            <span className="label-text-alt">Example: 123456789012345678</span>
          </div>
        </label>
        <div className="card-actions justify-end">
          <button
            className="btn btn-primary"
            onClick={onGenerateToken}
            disabled={sending}
          >
            {sending ? "Sending..." : hasSendToken ? "Resend Code" : "Send Code"}
          </button>
        </div>
        <label className="form-control w-full max-w-xs">
          <div className="label">
            <span className="label-text text-primary">Code</span>
            <span
              className="label-text-alt tooltip tooltip-left text-primary"
              data-tip="Paste the 5-digit code the bot sent you on Discord. It expires after 15 minutes."
            >
              <FaQuestionCircle size={24} />
            </span>
          </div>
          <input
            type="text"
            placeholder="Ex: 12345"
            className="input input-bordered w-full max-w-xs"
            value={inputToken}
            onChange={(e) => setInputToken(e.target.value.trim())}
          />
        </label>
        <div className="card-actions justify-end gap-2">
          {changing && (
            <button className="btn btn-ghost" onClick={resetForm}>
              Cancel
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={onValidateToken}
            disabled={!hasSendToken || validating}
          >
            {validating ? "Checking..." : changing ? "Switch account" : "Validate"}
          </button>
        </div>
      </div>
      <dialog id="discord_id_info" className="modal modal-middle">
        <div className="modal-box overflow-y-auto">
          <h3 className="font-bold text-lg text-primary">
            How to Copy a Discord User ID
          </h3>
          <div className="py-4">
            <div className="text-primary">1. Enable Developer Mode:</div>
            <div>Go to User Settings → Advanced → Enable Developer Mode.</div>
            <br />
            <div className="text-primary">2. Copy the User ID:</div>
            <div>Desktop: On Your Discord Profile click on "Copy User Id"</div>
            <br />
            <div>
              For Mobile: navigate to Crimson Kitten's server → Go to your
              Discord profile from the server's users list → tap on the three
              dots on the top right corner
            </div>
            <div className="text-center w-full btn btn-lg mt-4">
              <a
                href="https://support.discord.com/hc/en-us/articles/206346498-Where-can-I-find-my-User-Server-Message-ID"
                className="link link-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                View Official Discord Instructions
              </a>
            </div>
          </div>
          <div className="modal-action">
            <form method="dialog">
              {/* if there is a button in form, it will close the modal */}
              <button className="btn btn-error btn-outline">Close</button>
            </form>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default DiscordLinkWidget;
